import { ChevronDown, Copy04, Copy06, Edit01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import alertLocationIcon from 'src/assets/img/icons/alert-location.svg';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import CheckBox from 'src/components/CheckBox';
import TableCmp from 'src/components/TableCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import ActivityDropdownList, { IActivityDisplayItem } from 'src/components/V3DropdownCmp/ActivityDropdownList';
import QuickActionRadioList from 'src/components/V3DropdownCmp/QuickActionRadioList';
import { CLASSIFICATION_BADGE_CONFIG, getBadgeLabelByQuoteStatus, getBadgeTypeByQuoteStatus, STATUS } from 'src/constants/common';
import { RootState } from 'src/redux/store';
import { logError } from 'src/services/LoggingService';
import { getQuoteActivities } from 'src/services/QuoteService';
import { mapActivitiesToDisplayItems } from 'src/utils/activityFeed.helpers';
import { getDateWithSuffixFormat, getFormattedDate, getFormattedDateIgnoringTime, getFormattedNumber, copyToClipboard } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import { prepareQuoteActionInformation } from '../myQuote.helpers';

const LoadDetailRow = ({ data: dim, weightMeasurement, handlingUnitOptions }: { data: any; weightMeasurement: string; handlingUnitOptions?: any[] }) => {
    const handlingUnitLabel = dim?.handlingUnitDetails?.name || handlingUnitOptions?.find((o: any) => o.value === Number(dim?.handlingUnit))?.label;

    return (
        <>
            <td className="w-[24%]">{handlingUnitLabel || dim?.handlingUnit || '-'}</td>
            <td className="w-[14%]">{dim?.handlingUnitNo || 1}</td>
            <td className="w-[25%]">{dim?.itemLength && dim?.width && dim?.height ? `${dim.itemLength}"x${dim.width}"x${dim.height}"` : '-'}</td>
            <td className="w-[16%]">{dim?.freightClass || '-'}</td>
            <td className="w-[21%] text-right">{dim?.totalWeight ? `${getFormattedNumber(dim.totalWeight, false)} ${weightMeasurement}` : '-'}</td>
        </>
    );
};

const QuoteCustomerRow = ({
    onRowClick,
    showLoader,
    editQuote,
    selectedEditQuoteId,
    duplicateQuote,
    selectedDuplicateQuoteId,
    onPickupDateWarningClick,
    data,
    headCells,
    selectedQuoteIds,
    onToggleSelect,
    actionColumnWidthClass,
}: any) => {
    const { t } = useTranslation();
    const codeRef = useRef<HTMLHeadingElement>(null);
    const fullNameRef = useRef<HTMLDivElement>(null);
    const shipperFullAddressRef = useRef<HTMLParagraphElement>(null);
    const consigneeFullAddressRef = useRef<HTMLParagraphElement>(null);
    const divisionRef = useRef<HTMLParagraphElement>(null);
    const appearance = useSelector((state: RootState) => state.basic.appearance);
    const handlingUnitOptions = useSelector((state: RootState) => state.basic.handlingUnitOptions);
    const selectedSet = useMemo(() => new Set(selectedQuoteIds ?? []), [selectedQuoteIds]);
    const [isFromTooltipOpen, setIsFromTooltipOpen] = useState(false);
    const [isToTooltipOpen, setIsToTooltipOpen] = useState(false);
    const [isLoadDetailsTooltipOpen, setIsLoadDetailsTooltipOpen] = useState(false);
    const [isLatestActivityTooltipOpen, setIsLatestActivityTooltipOpen] = useState(false);
    const [isQuickActionsTooltipOpen, setIsQuickActionsTooltipOpen] = useState(false);
    const [quickActionsCloseKey, setQuickActionsCloseKey] = useState(0);
    const [selectedQuoteActionId, setSelectedQuoteActionId] = useState<number | null>(null);
    const [quoteActivities, setQuoteActivities] = useState<any[]>([]);
    const [isActivityLoading, setIsActivityLoading] = useState(false);

    // Mirrors QuotingHub/QuotingDashboard/quotingDashboard.helpers.ts's prepareQuoteActionInformation on the WAL side.
    const quoteActions = useMemo(() => prepareQuoteActionInformation(data, t, appearance?.timezone), [data, t, appearance?.timezone]);
    const selectedQuoteAction = useMemo(() => quoteActions.find((action) => action.actionId === selectedQuoteActionId) || quoteActions[0], [quoteActions, selectedQuoteActionId]);

    // Remounting the TooltipCmp (via its key) closes the popover once the action is handled.
    const handleQuoteActionButtonClick = () => {
        setQuickActionsCloseKey((k) => k + 1);

        if (selectedQuoteAction?.actionKey === 'update_pickup_date') {
            onPickupDateWarningClick?.(data);
        }
    };

    useEffect(() => {
        if (!isLatestActivityTooltipOpen) return;

        setIsActivityLoading(true);
        getQuoteActivities({ entityType: 'quote', entityId: data.id })
            .then((response: any) => {
                setQuoteActivities(response?.data?.activities || []);
            })
            .catch(logError)
            .finally(() => setIsActivityLoading(false));
    }, [isLatestActivityTooltipOpen]);

    const activityDisplayItems = useMemo<IActivityDisplayItem[]>(() => mapActivitiesToDisplayItems(quoteActivities), [quoteActivities]);

    const classificationBadge = useMemo(() => {
        const config = CLASSIFICATION_BADGE_CONFIG[data.classification];
        if (!config) return null;

        return (
            <BadgeCmp style="modern" type={config.type}>
                {t(config.label)}
            </BadgeCmp>
        );
    }, [data.classification, t]);

    const duplicateButton = useMemo(() => {
        // duplicatingQuoteId comes from useDuplicateQuote (set for the whole fetch → navigate span).
        if (selectedDuplicateQuoteId && selectedDuplicateQuoteId === data.id) {
            return <span className="loading loading-spinner lg:w-4 lg:h-4 h-3 w-3 text-xs"></span>;
        }

        return (
            <TooltipCmp message={t('Common.Duplicate')}>
                <Copy04 className="w-4 h-4 relative cursor-pointer" onClick={() => duplicateQuote(data)} />
            </TooltipCmp>
        );
    }, [selectedDuplicateQuoteId, data.id, duplicateQuote, t]);

    const editButton = useMemo(() => {
        if (showLoader && selectedEditQuoteId && selectedEditQuoteId === data.id) {
            return <span className="loading loading-spinner lg:w-4 lg:h-4 h-3 w-3 text-xs"></span>;
        }

        if (data.status !== STATUS.WON && data.status !== STATUS.LOST && data.status !== STATUS.BOOKED) {
            return (
                <TooltipCmp message={t('QuotingHub.Edit_Quote')}>
                    <Edit01 className="w-4 h-4 relative cursor-pointer" onClick={() => editQuote(data)} />
                </TooltipCmp>
            );
        }

        return null;
    }, [showLoader, selectedEditQuoteId, data.id, data.status, editQuote, t]);

    const shipperFullAddress = useMemo(() => {
        let fullAddress = '';
        if (data?.shipperCity) fullAddress += `${data?.shipperCity}, `;
        if (data?.shipperStateCode) fullAddress += `${data?.shipperStateCode}, `;
        if (data?.shipperPostal) fullAddress += `${data?.shipperPostal}, `;
        if (data?.shipperCountry) fullAddress += `${data?.shipperCountry}`;

        return fullAddress;
    }, [data?.shipperCity, data?.shipperStateCode, data?.shipperPostal, data?.shipperCountry]);

    const shipperShortAddress = useMemo(() => {
        let shortAddress = '';

        if (data?.shipperCity) {
            shortAddress += `${data?.shipperCity}`;
        }

        if (data?.shipperStateCode) {
            if (shortAddress) {
                shortAddress += `, ${data?.shipperStateCode}`;
            } else {
                shortAddress += `${data?.shipperStateCode}`;
            }
        }

        return shortAddress;
    }, [data?.shipperCity, data?.shipperStateCode]);

    const consigneeFullAddress = useMemo(() => {
        let fullAddress = '';
        if (data?.consigneeCity) fullAddress += `${data?.consigneeCity}, `;
        if (data?.consigneeStateCode) fullAddress += `${data?.consigneeStateCode}, `;
        if (data?.consigneePostal) fullAddress += `${data?.consigneePostal}, `;
        if (data?.consigneeCountry) fullAddress += `${data?.consigneeCountry}`;

        return fullAddress;
    }, [data?.consigneeCity, data?.consigneeStateCode, data?.consigneePostal, data?.consigneeCountry]);

    const billingAccountCurrencyLabel = useMemo(() => (data?.billingAccountCurrency ? data.billingAccountCurrency.toUpperCase() : '-'), [data?.billingAccountCurrency]);

    // Full detail shown in the hover tooltip — the cell itself only shows the currency (CAD/USD).
    // billingAccountName (displayName) already reads as "CAD Billing Account - 22222", so it's shown once, not rebuilt.
    const billingAccountDetail = useMemo(
        () => (
            <div className="text-left space-y-1">
                <p className="text-xs font-medium text-grayLight900">{data?.billingAccountName || '-'}</p>
                {data?.billingAccountShortCode && <p className="text-xs text-gray500">Short Code: {data.billingAccountShortCode}</p>}
            </div>
        ),
        [data?.billingAccountName, data?.billingAccountShortCode]
    );

    const consigneeShortAddress = useMemo(() => {
        let shortAddress = '';

        if (data?.consigneeCity) {
            shortAddress += `${data?.consigneeCity}`;
        }

        if (data?.consigneeStateCode) {
            if (shortAddress) {
                shortAddress += `, ${data?.consigneeStateCode}`;
            } else {
                shortAddress += `${data?.consigneeStateCode}`;
            }
        }

        return shortAddress;
    }, [data?.consigneeCity, data?.consigneeStateCode]);

    // Map column id → JSX for dynamic rendering
    const tableCells = useMemo(
        () => [
            {
                key: 'code',
                htmlDynamic: (
                    <td className="max-w-px min-w-[190px] table-cell-with-tooltip" onClick={onRowClick(data)}>
                        <div className="flex overflow-hidden h-full w-full">
                            <div className="w-fit max-w-full h-full">
                                <div className={`self-center flex items-center h-full hover:bg-utilityGray200 px-2 min-w-[140px] ${isLatestActivityTooltipOpen ? 'bg-utilityGray100' : ''}`}>
                                    <TruncateTextCmp refName={codeRef} message={`#${data.code}`}>
                                        <div ref={codeRef} className="text-utilityBlack800 font-semibold truncate cursor-pointer" onClick={onRowClick(data)}>
                                            #{data.code}
                                        </div>
                                    </TruncateTextCmp>
                                    <div onClick={(e) => e.stopPropagation()} className="h-full">
                                        <TooltipCmp
                                            TooltipCustomClass="tooltipV3-content-wrap"
                                            isArrow={false}
                                            isTooltipInteractive
                                            parentClassName="tooltip-content-with-scroll-wrap w-[400px] space-y-2 text-left"
                                            openOnClick
                                            position="bottom"
                                            onOpenChange={setIsLatestActivityTooltipOpen}
                                            message={<ActivityDropdownList items={activityDisplayItems} isLoading={isActivityLoading} listTitle="Latest Activity" />}
                                        >
                                            <div className={`table-icon-wrap flex-none cursor-pointer flex items-center h-full p-1 ${isLatestActivityTooltipOpen ? '!opacity-100' : ''}`}>
                                                <ChevronDown className="w-4 h-4 text-grayLight600" />
                                            </div>
                                        </TooltipCmp>
                                    </div>
                                </div>
                            </div>
                            {quoteActions.length > 0 && (
                                <div className={`h-full flex gap-x-1 hover:bg-utilityGray200 group ${isQuickActionsTooltipOpen ? 'bg-utilityGray200' : ''}`} onClick={(e) => e.stopPropagation()}>
                                    {/* Wrapper stops row navigation; tooltip still opens internally */}
                                    <TooltipCmp
                                        key={quickActionsCloseKey}
                                        TooltipCustomClass="tooltipV3-content-wrap"
                                        isArrow={false}
                                        position="bottom"
                                        openOnClick
                                        isTooltipInteractive
                                        parentClassName="tooltip-content-with-scroll-wrap space-y-1 w-[400px] text-left space-y-2.5"
                                        onOpenChange={setIsQuickActionsTooltipOpen}
                                        message={
                                            <>
                                                <p className="text-xs text-grayLight900 font-medium">{t('QuotingHub.Common.Quick_Actions')}</p>
                                                <QuickActionRadioList
                                                    inputName={`quote-actions-${data?.id}`}
                                                    items={quoteActions.map((action) => ({
                                                        id: `quote-action-${data?.id}-${action.actionId}`,
                                                        value: String(action.actionId),
                                                        labelTitle: action.actionCaption,
                                                        labelSubtitle: action.actionDescription,
                                                        showErrorIcon: true,
                                                        isChecked: selectedQuoteAction?.actionId === action.actionId,
                                                        onChangeFunc: () => setSelectedQuoteActionId(action.actionId),
                                                    }))}
                                                />
                                                <div className="text-right">
                                                    <ButtonCmp
                                                        type="button"
                                                        className="btn-outline-primary"
                                                        size="square-xs"
                                                        onClick={(e: any) => {
                                                            e.stopPropagation();
                                                            handleQuoteActionButtonClick();
                                                        }}
                                                    >
                                                        {selectedQuoteAction?.actionButtonName}
                                                    </ButtonCmp>
                                                </div>
                                            </>
                                        }
                                    >
                                        <div className="flex h-full items-center cursor-pointer px-1.5">
                                            <div className="w-4 h-4 relative">
                                                <img src={alertLocationIcon} alt="alert-location" className="w-full h-full text-primary flex-none" />
                                            </div>
                                            <div className={`table-icon-wrap flex-none ${isQuickActionsTooltipOpen ? '!opacity-100' : ''}`}>
                                                <ChevronDown className="w-4 h-4 text-grayLight600" />
                                            </div>
                                        </div>
                                    </TooltipCmp>
                                </div>
                            )}
                        </div>
                    </td>
                ),
            },
            {
                key: 'userData',
                htmlDynamic: (
                    <td className="max-w-px min-w-[155px]" onClick={onRowClick(data)}>
                        <div className="flex items-center gap-x-2 overflow-hidden h-full">
                            {data?.quoteShareMember && (
                                <div className="notification-mark-wrap w-2.5 h-2.5 flex-none">
                                    <span className="notification-dot"></span>
                                </div>
                            )}
                            <TruncateTextCmp refName={fullNameRef} message={`${data?.userData?.firstName ?? ''} ${data?.userData?.lastName ?? ''}`}>
                                <div ref={fullNameRef} className="truncate">
                                    {data?.userData?.firstName} {data?.userData?.lastName}
                                </div>
                            </TruncateTextCmp>
                        </div>
                    </td>
                ),
            },
            {
                key: 'status',
                htmlDynamic: (
                    <td className="max-w-[6.95%] min-w-[125px]" onClick={onRowClick(data)}>
                        <BadgeCmp style="modern" type={getBadgeTypeByQuoteStatus(data?.status)}>
                            {getBadgeLabelByQuoteStatus(data?.status)}
                        </BadgeCmp>
                    </td>
                ),
            },
            {
                key: 'classification',
                htmlDynamic: (
                    <td className="max-w-[6.95%] min-w-[115px]" onClick={onRowClick(data)}>
                        {classificationBadge}
                    </td>
                ),
            },
            {
                key: 'division',
                htmlDynamic: (
                    <td className="max-w-px min-w-[150px]" onClick={onRowClick(data)}>
                        <TruncateTextCmp refName={divisionRef} message={data?.divisionName || '-'}>
                            <p className="truncate" ref={divisionRef}>
                                {data?.divisionName || '-'}
                            </p>
                        </TruncateTextCmp>
                    </td>
                ),
            },
            {
                key: 'customerBillingAccount',
                htmlDynamic: (
                    <td className="max-w-[6.95%] min-w-[125px]" onClick={onRowClick(data)}>
                        {data?.billingAccountCurrency ? (
                            <TooltipCmp message={billingAccountDetail} childrenClassName="flex">
                                <span className="truncate cursor-default">{billingAccountCurrencyLabel}</span>
                            </TooltipCmp>
                        ) : (
                            <p className="truncate">-</p>
                        )}
                    </td>
                ),
            },
            {
                key: 'shipperFullAddress',
                htmlDynamic: (
                    <td className="max-w-px min-w-[150px] table-cell-with-tooltip" onClick={onRowClick(data)}>
                        <div className={`flex items-center h-full table-cell-clickable table-col-tooltip-space-common-xs ${isFromTooltipOpen ? ' !bg-utilityGray200' : ''}`}>
                            {/* City/state ONLY — 1:1 with wal-frontend-v2's Quoting Dashboard listing
                                (QuotingDashboardRow renders `shipperShortAddress`). The shipper NAME is
                                deliberately NOT in the cell; it is the tooltip header below. BE sorts this
                                column on the same city so order matches what's displayed. */}
                            <p className="font-medium truncate cursor-pointer">{shipperShortAddress}</p>
                            {/* Tooltip opens only on chevron click, not the whole cell */}
                            <div onClick={(e) => e.stopPropagation()} className="h-full">
                                <TooltipCmp
                                    TooltipCustomClass="tooltipV3-content-wrap"
                                    isArrow={false}
                                    position="bottom"
                                    openOnClick
                                    isTooltipInteractive
                                    parentClassName="tooltip-content-with-scroll-wrap w-[350px] text-left space-y-1"
                                    onOpenChange={setIsFromTooltipOpen}
                                    message={
                                        <>
                                            {data?.shipperCompanyName && <h6 className="text-grayLight900 text-xs leading-[1.5] font-medium">{data.shipperCompanyName}</h6>}
                                            <div className="tooltip-content-with-scroll flex justify-between gap-x-2 ">
                                                <TruncateTextCmp refName={shipperFullAddressRef} message={shipperFullAddress || ''}>
                                                    <p ref={shipperFullAddressRef} className="text-xs leading-[1.5] font-medium text-gray500 line-clamp-2">
                                                        {shipperFullAddress}
                                                    </p>
                                                </TruncateTextCmp>
                                                <TooltipCmp TooltipCustomClass="tooltipV3-content-wrap" message={t('Common.Copy')}>
                                                    <Copy06
                                                        className="w-4 h-4 text-primary cursor-pointer flex-none "
                                                        onClick={(e: any) => {
                                                            e.stopPropagation();
                                                            copyToClipboard(shipperFullAddress);
                                                            WalToast.copy(t('Common.Copied_to_clipboard') as string, '', 'copy-msg');
                                                        }}
                                                    />
                                                </TooltipCmp>
                                            </div>
                                        </>
                                    }
                                >
                                    <div className={`table-icon-wrap flex-none cursor-pointer flex items-center h-full p-1 ${isFromTooltipOpen ? '!opacity-100' : ' '}`}>
                                        <ChevronDown className="w-4 h-4 text-grayLight600 flex-none " />
                                    </div>
                                </TooltipCmp>
                            </div>
                        </div>
                    </td>
                ),
            },
            {
                key: 'consigneeFullAddress',
                htmlDynamic: (
                    <td className="max-w-px min-w-[150px] table-cell-with-tooltip" onClick={onRowClick(data)}>
                        <div className={`flex items-center h-full table-cell-clickable table-col-tooltip-space-common-xs ${isToTooltipOpen ? ' !bg-utilityGray200' : ''}`}>
                            {/* City/state ONLY — same display rule as From above (name lives in the tooltip). */}
                            <span className="font-medium truncate cursor-pointer">{consigneeShortAddress}</span>
                            {/* Tooltip opens only on chevron click, not the whole cell */}
                            <div onClick={(e) => e.stopPropagation()} className="h-full">
                                <TooltipCmp
                                    TooltipCustomClass="tooltipV3-content-wrap"
                                    isArrow={false}
                                    position="bottom"
                                    openOnClick
                                    isTooltipInteractive
                                    parentClassName="tooltip-content-with-scroll-wrap w-[350px] text-left space-y-1"
                                    onOpenChange={setIsToTooltipOpen}
                                    message={
                                        <>
                                            {data?.consigneeCompanyName && <h6 className="text-grayLight900 text-xs leading-[1.5] font-medium">{data.consigneeCompanyName}</h6>}
                                            <div className="tooltip-content-with-scroll flex justify-between gap-x-2 ">
                                                <TruncateTextCmp refName={consigneeFullAddressRef} message={consigneeFullAddress || ''}>
                                                    <p ref={consigneeFullAddressRef} className="text-xs leading-[1.5] font-medium text-gray500 line-clamp-2">
                                                        {consigneeFullAddress}
                                                    </p>
                                                </TruncateTextCmp>
                                                <TooltipCmp TooltipCustomClass="tooltipV3-content-wrap" message={t('Common.Copy')}>
                                                    <Copy06
                                                        className="w-4 h-4 text-primary cursor-pointer flex-none "
                                                        onClick={(e: any) => {
                                                            e.stopPropagation();
                                                            copyToClipboard(consigneeFullAddress);
                                                            WalToast.copy(t('Common.Copied_to_clipboard') as string, '', 'copy-msg');
                                                        }}
                                                    />
                                                </TooltipCmp>
                                            </div>
                                        </>
                                    }
                                >
                                    <div className={`table-icon-wrap flex-none cursor-pointer flex items-center h-full p-1 ${isToTooltipOpen ? '!opacity-100' : ''}`}>
                                        <ChevronDown className="w-4 h-4 text-grayLight600 flex-none " />
                                    </div>
                                </TooltipCmp>
                            </div>
                        </div>
                    </td>
                ),
            },
            {
                key: 'stopsCount',
                htmlDynamic: (() => {
                    // stopsCount (from getQuotes.controller.ts's correlated subquery) already counts
                    // the shipper/consignee bookends — > 2 means at least one genuine customer-visible
                    // middle stop, same convention as QuoteDetailMetaDataTable.tsx's "Quote Type" cell.
                    const stopsTotal = Number(data?.stopsCount ?? 0);
                    const intermediateStopCount = stopsTotal > 2 ? stopsTotal - 2 : 0;

                    return (
                        <td data-test-id="wal-QuotingDashboard-stopsCount" className="max-w-[6.95%] min-w-[110px]" onClick={onRowClick(data)}>
                            <span>{intermediateStopCount > 0 ? t('MyQuote.Stops_Column_Count', { count: intermediateStopCount }) : t('MyQuote.Stops_Direct_Option')}</span>
                        </td>
                    );
                })(),
            },
            {
                key: 'updatedAt',
                htmlDynamic: (
                    <td data-test-id="wal-QuotingDashboard-updatedAt" className="max-w-[6.95%] min-w-[145px]" onClick={onRowClick(data)}>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: data.updatedAt ? getDateWithSuffixFormat(getFormattedDate(data.updatedAt, `MMM Do, hh:mm A`, true, true, appearance)) : '-',
                            }}
                        />
                    </td>
                ),
            },
            {
                key: 'createdAt',
                htmlDynamic: (
                    <td data-test-id="wal-QuotingDashboard-createdAt" className="max-w-[6.95%] min-w-[175px]" onClick={onRowClick(data)}>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: data.createdAt ? getDateWithSuffixFormat(getFormattedDate(data.createdAt, `MMM Do, hh:mm A`, true, true, appearance)) : '-',
                            }}
                        />
                    </td>
                ),
            },
            {
                key: 'pickupDate',
                htmlDynamic: (
                    <td data-test-id="wal-QuotingDashboard-pickupDate" className="max-w-[6.95%] min-w-[175px]" onClick={onRowClick(data)}>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: data.pickupDate ? getDateWithSuffixFormat(getFormattedDateIgnoringTime(data.pickupDate, 'ddd, MMM Do, YYYY', true, true, true)) : '-',
                            }}
                        />
                    </td>
                ),
            },
            {
                key: 'totalWeight',
                htmlDynamic: (
                    <td data-test-id="wal-QuotingDashboard-totalWeight" className="max-w-[6.95%] min-w-[135px] " onClick={onRowClick(data)}>
                        {data?.totalWeight && <span className="">{`${getFormattedNumber(data.totalWeight, false)}  ${data.weightMeasurement ? data.weightMeasurement?.toLowerCase() : 'lbs'}`}</span>}
                    </td>
                ),
            },
            {
                key: 'totalUnits',
                htmlDynamic: (() => {
                    const dims = (() => {
                        try {
                            const raw = data?.dimensions ?? data?.loadDetails?.dimensions;

                            return Array.isArray(raw) ? raw : typeof raw === 'string' ? JSON.parse(raw) : [];
                        } catch {
                            return [];
                        }
                    })();

                    const loadWeightUnit = (data?.weightMeasurement || data?.loadDetails?.weightMeasurement || 'lbs').toLowerCase();
                    const totalLineItems = dims.length > 0 ? dims.reduce((sum: number, d: any) => sum + (Number(d.handlingUnitNo) || 0), 0) : '-';
                    const totalLinearFt = data?.linearFootage ? data.linearFootage : '-';
                    const totalWeightVal = data?.totalWeight ?? 0;
                    const tooltipTotalWeightDisplay = `${getFormattedNumber(totalWeightVal, false)} ${loadWeightUnit}`;

                    const cellText = data?.classification === 'ltl' && data?.loadDetails?.handlingUnits ? data.loadDetails.handlingUnits : data?.primaryEquipmentTypeFullName || '-';

                    return (
                        <td data-test-id="wal-QuotingDashboard-totalUnits" className={`max-w-px min-w-[135px] ${dims?.length > 0 ? 'table-cell-with-tooltip' : ''}`} onClick={onRowClick(data)}>
                            {dims?.length > 0 ? (
                                <div className={`flex items-center h-full table-cell-clickable table-col-tooltip-space-common-xs ${isLoadDetailsTooltipOpen ? ' !bg-utilityGray200' : ''}`}>
                                    <p className="truncate min-w-0 cursor-pointer">{cellText}</p>
                                    {/* Tooltip opens only on chevron click, not the whole cell */}
                                    <div onClick={(e) => e.stopPropagation()} className="h-full">
                                        <TooltipCmp
                                            TooltipCustomClass="tooltipV3-content-wrap"
                                            isArrow={false}
                                            position="bottom"
                                            openOnClick
                                            isTooltipInteractive
                                            parentClassName="tooltip-content-with-scroll-wrap w-[480px] space-y-2 text-left"
                                            onOpenChange={setIsLoadDetailsTooltipOpen}
                                            message={
                                                <>
                                                    <p className="text-xs text-grayLight900 font-medium">{t('Common.Line_Items')}</p>
                                                    <div className="tooltip-content-with-scroll  pb-1">
                                                        <div className="table-box-wrap">
                                                            <div className="table-box-container overflow-hidden">
                                                                <div className="overflow-x-auto custom-scrollbar scrollbar-hide table-without-title-header table-without-pagination">
                                                                    <TableCmp
                                                                        headCells={[
                                                                            { id: 'handlingUnitName', name: t('Common.Handling_Unit'), visible: true },
                                                                            { id: 'handlingUnitNo', name: '№', visible: true },
                                                                            { id: 'dimensions', name: t('Common.Dimensions'), visible: true },
                                                                            { id: 'freightClass', name: 'FC', visible: true },
                                                                            { id: 'totalWeight', name: t('Common.Weight'), visible: true, headCellsTextClass: 'justify-end' },
                                                                        ]}
                                                                        tableDataArr={dims}
                                                                        TableRowCmp={LoadDetailRow}
                                                                        tableRowCmpProps={{ weightMeasurement: loadWeightUnit, handlingUnitOptions }}
                                                                        tableRowHeight="xs"
                                                                        footerDataArr={[{}]}
                                                                        tableBodyRowClass="last:!border-utilityGray200"
                                                                        tableFooterRowClass="!border-b-0"
                                                                        TableFooterRowCmp={() => (
                                                                            <td colSpan={5} className="!p-0">
                                                                                <TableCmp
                                                                                    tableRowHeight="xs"
                                                                                    tableDataArr={[{}]}
                                                                                    tableBodyRowClass="divide-x divide-utilityGray200"
                                                                                    TableRowCmp={() => (
                                                                                        <>
                                                                                            <td className="w-[33.33%] text-grayLight900 font-medium ">
                                                                                                <div className="h-full flex items-center gap-x-2 justify-between">
                                                                                                    <p>{t('Common.Line_Item')}</p>
                                                                                                    <p> {totalLineItems}</p>
                                                                                                </div>
                                                                                            </td>
                                                                                            <td className="w-[33.33%] text-grayLight900 font-medium ">
                                                                                                <div className="h-full flex items-center gap-x-2 justify-between">
                                                                                                    <p>{t('Common.Total_Linear_Ft')}</p>
                                                                                                    <p>{totalLinearFt}</p>
                                                                                                </div>
                                                                                            </td>
                                                                                            <td className="w-[33.33%] text-grayLight900 font-medium ">
                                                                                                <div className="h-full flex items-center gap-x-2 justify-between">
                                                                                                    <p>{t('Common.Total_Weight')}</p>
                                                                                                    <p>{tooltipTotalWeightDisplay}</p>
                                                                                                </div>
                                                                                            </td>
                                                                                        </>
                                                                                    )}
                                                                                />
                                                                            </td>
                                                                        )}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            }
                                        >
                                            <div className={`table-icon-wrap flex-none cursor-pointer flex items-center h-full p-1 ${isLoadDetailsTooltipOpen ? '!opacity-100' : ' '}`}>
                                                <ChevronDown className="w-4 h-4 text-grayLight600 flex-none " />
                                            </div>
                                        </TooltipCmp>
                                    </div>
                                </div>
                            ) : (
                                <p className="truncate min-w-0" onClick={onRowClick(data)}>
                                    {cellText}
                                </p>
                            )}
                        </td>
                    );
                })(),
            },
            {
                key: 'action',
                htmlDynamic: (
                    <td className="w-[98px] max-w-[98px] table-cell-with-tooltip">
                        <div className={`flex items-center justify-end gap-2 h-full table-col-tooltip-space-common-xs ml-auto ${actionColumnWidthClass || ''}`}>
                            <div className="w-4 h-4 flex items-center justify-center flex-none">{duplicateButton}</div>
                            {/* Edit action hidden for now (not removed) — hide via CSS only. */}
                            <div className="hidden w-4 h-4 items-center justify-center flex-none">{editButton}</div>
                        </div>
                    </td>
                ),
            },
        ],
        [
            data,
            showLoader,
            selectedEditQuoteId,
            selectedDuplicateQuoteId,
            appearance,
            isFromTooltipOpen,
            isToTooltipOpen,
            isLoadDetailsTooltipOpen,
            isLatestActivityTooltipOpen,
            activityDisplayItems,
            isActivityLoading,
            quoteActions,
            selectedQuoteAction,
            isQuickActionsTooltipOpen,
            quickActionsCloseKey,
            onPickupDateWarningClick,
            actionColumnWidthClass,
            billingAccountCurrencyLabel,
            billingAccountDetail,
        ]
    );

    return (
        <>
            {headCells
                ?.filter((cell: any) => cell.visible)
                .map((cell: any) => {
                    if (cell.id === 'checkbox') {
                        return (
                            <React.Fragment key="checkbox">
                                <td className="w-[40px] min-w-[40px] max-w-[40px] sticky left-0 bg-inherit z-[1] table-cell-with-tooltip" onClick={(e) => e.stopPropagation()}>
                                    <CheckBox
                                        id={`select-quote-${data.id}`}
                                        checked={selectedSet.has(data.id)}
                                        onChangeFunc={(e: any) => {
                                            e.stopPropagation();
                                            onToggleSelect?.(data.id);
                                        }}
                                        name="selectQuote"
                                        value={String(data.id)}
                                        parentClassName="table-col-tooltip-space-common-xs h-full items-center"
                                    />
                                </td>
                            </React.Fragment>
                        );
                    }

                    const matched = tableCells.find((tCell) => tCell.key === cell.id);

                    if (!matched) return null;

                    return <React.Fragment key={cell.id}>{matched.htmlDynamic}</React.Fragment>;
                })}
        </>
    );
};

export default QuoteCustomerRow;
